import { GS3HeaderLogo } from "@/assets/images";
import { useMeModulesQuery } from "@shared/api";
import { useUserContext } from "@shared/contexts";
import {
  handleOperationalSidebarClick,
  useOperationalModulePicker,
} from "@shared/contexts/operationalModulePicker";
import { useAdminAccessQuery } from "@shared/hooks";
import { useLocalization } from "@i18n";
import {
  Sidebar as SidebarBase,
  SidebarTrigger,
} from "@telluscompany/design-system";
import { Settings, UserCog } from "lucide-react";

import {
  FALLBACK_MODULE_ICON,
  OPERATIONAL_MODULE_PRESENTATION,
} from "./operationalModules.config";

export const Sidebar = () => {
  const { translate } = useLocalization();
  const { openPicker } = useOperationalModulePicker();
  const { user: me } = useUserContext();
  const { hasAdminAccess } = useAdminAccessQuery();
  const { data: operationalModules } = useMeModulesQuery();

  const isMasterReader = me?.role === "owner" || me?.role === "ownerViewer";

  // Módulos operacionais do tenant ativo (GET /v1/user/me/modules). O backend
  // é a fonte da ordem (displayOrder) e da publicação (isReleased); o front só
  // mapeia para rótulo i18n + ícone do DS e respeita a ordem recebida.
  //
  // Clicabilidade operacional: publicação + acesso calculado pelo backend.
  // Itens sem rota frontend conhecida também permanecem desabilitados.
  const moduleGroupItems = [...(operationalModules ?? [])]
    .sort((first, second) => first.displayOrder - second.displayOrder)
    .map((operationalModule) => {
      const presentation =
        OPERATIONAL_MODULE_PRESENTATION[operationalModule.module];
      const routePath = presentation?.routePath;
      const canNavigate = Boolean(
        operationalModule.isReleased && operationalModule.hasAccess && routePath,
      );

      return {
        icon: presentation?.icon ?? FALLBACK_MODULE_ICON,
        title: presentation
          ? translate(presentation.i18nKey)
          : operationalModule.module,
        url: routePath ?? "#",
        disabled: !canNavigate,
      };
    });

  // Gating de UI — espelha o backend para evitar que o usuário clique e
  // receba 403. Ver `roles-ui-gating.mdc` §3.1 (esconder controles, não
  // disabled) e `useAdminAccessQuery` para a derivação de `hasAdminAccess`
  // (cobre owner / ownerViewer / complexAdmin via flag do back).
  const canSeeManagement = isMasterReader;
  const canSeeAdmin = isMasterReader || hasAdminAccess;

  // Defesa em profundidade: itens sem permissão NÃO entram no array passado
  // para `SidebarBase`. Assim o gating não depende exclusivamente da prop
  // `hidden` do design-system (que é o filtro interno da lib) — se o
  // contrato do componente mudar, itens proibidos continuam fora do DOM.
  // Defesa secundária no back: rotas /management e /admin têm loaders que
  // bloqueiam acesso direto via URL (ver `roles-ui-gating.mdc` §3.3).
  const adminGroupItems = [
    ...(canSeeManagement
      ? [
          {
            icon: Settings,
            title: translate("common.management"),
            url: "/management",
          },
        ]
      : []),
    ...(canSeeAdmin
      ? [
          {
            icon: UserCog,
            title: translate("common.admin"),
            url: "/admin",
          },
        ]
      : []),
  ];

  return (
    <>
      <div
        className="contents"
        onClickCapture={(event) =>
          handleOperationalSidebarClick(event, openPicker)
        }
      >
        <SidebarBase
          topOffset={80}
          collapsedLogo={GS3HeaderLogo}
          expandedLogo={GS3HeaderLogo}
          groups={[
            // Módulos operacionais no TOPO (espelha o sidebar-gs3 do Figma).
            // Só entra como group quando há itens, evitando um group vazio
            // enquanto a query carrega.
            ...(moduleGroupItems.length > 0
              ? [{ items: moduleGroupItems }]
              : []),
            // Grupo administrativo — sempre passado como ÚLTIMO `group` para
            // `SidebarBase`. A regra em `src/index.css`
            // (`[data-slot="sidebar-content"] > div:last-child`) empurra o
            // último group para o rodapé com `margin-top: auto`, espelhando
            // o sidebar-gs3 do Figma onde Admin fica ancorado embaixo.
            { items: adminGroupItems },
          ]}
          version="2.18.0"
        />
      </div>
      <SidebarTrigger />
    </>
  );
};
